import {
    AfterViewInit, ChangeDetectorRef,
    Component,
    Input,
    OnChanges, OnInit,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { AbstractControl, UntypedFormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DateAdapter, ErrorStateMatcher, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import * as moment from 'moment';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { BaseComponent } from '../../../base.component';
import { Option } from '../../../interfaces/option.interface';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { debounceTime, distinctUntilChanged, startWith, takeUntil } from 'rxjs/operators';
import { filter } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { SelectComponent } from '../../selects/select/select.component';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { NgIf } from '@angular/common';

export const MY_FORMATS = {
    parse: {
        dateInput: 'L'
    },
    display: {
        monthYearA11yLabel: 'MMMM YYYY',
        monthYearLabel: 'MMM YYYY',
        dateInput: 'L',
    }
};

@Component({
    selector: 'app-date-time-input-field',
    templateUrl: './date-time-input-field.component.html',
    styleUrls: ['./date-time-input-field.component.scss'],
    providers: [
        { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
        { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    ],
    standalone: true,
    imports: [NgIf, MatFormFieldModule, MatInputModule, MatDatepickerModule, FormsModule, ReactiveFormsModule, SelectComponent, TranslateModule]
})
export class DateTimeInputFieldComponent extends BaseComponent implements OnInit, OnChanges, AfterViewInit {
    @Input() placeholder: string;
    @Input() control: AbstractControl;
    @Input() required = false;
    @Input() id = '';
    @Input() min: string;
    @Input() defaultTime: string;
    @Input() disabled = false;
    @Input() hint: string;
    @Input() hintClass: string;
    @Input() times: Option[] = [];

    @ViewChild('picker', {static: false}) picker: MatDatepicker<Date>;

    dateControl: UntypedFormControl = new UntypedFormControl(null);
    timeControl: UntypedFormControl = new UntypedFormControl(null);
    matcher: ErrorStateMatcher = new ErrorStateMatcher();
    minDate = moment().startOf('day');

    constructor(private adapter: DateAdapter<any>,
                private cd: ChangeDetectorRef) {
        super();
        this.adapter.setLocale(moment.locale());
        this.timeControl.disable();
    }

    ngOnInit() {
        if (!this.control.touched) {
            this.dateControl.markAsUntouched();
        }

        this.subscribeControlValueChanges();
        this.subscribeTimeControlValueChanges();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.disabled) {
            if (this.disabled) {
                this.dateControl.disable();
                this.timeControl.disable();
            } else {
                this.dateControl.enable();
            }
        }

        if (changes.min && !!this.min) {
            this.minDate = moment(this.min).startOf('day');
        }

        if (changes.times && this.times?.length > 0) {
            this.timeControl.updateValueAndValidity();
        }

        if (changes.defaultTime && !!this.dateControl.value) {
            this.timeControl.setValue(this.defaultTime);
            const date = moment(this.dateControl.value).format('YYYY-MM-DD') + ' ' + this.defaultTime;
            this.control.setValue(moment(date));
        }
    }

    ngAfterViewInit() {
        this.matcher = new MyDateTimePickerMatcher(this.control, this.picker);
    }

    onSelectChange(value: number) {
        const date = moment(this.dateControl.value).format('YYYY-MM-DD') + ' ' + value;
        this.control.setValue(moment(date));
    }

    onChanged() {
        if (this.defaultTime && !this.timeControl.value) {
            this.timeControl.setValue(this.defaultTime);
        }

        const date = moment(this.dateControl.value).format('YYYY-MM-DD') + ' ' + this.timeControl.value;
        this.control.setValue(moment(date));
    }

    subscribeControlValueChanges() {
        this.control.valueChanges
            .pipe(
                startWith(this.control.value),
                filter(date => !!date),
                debounceTime(500),
                distinctUntilChanged(),
                takeUntil(this.destroyed$)
            ).subscribe((date) => {
                if (!moment(date).isSame(moment(this.dateControl.value).format('YYYY-MM-DD') + ' ' + this.timeControl.value)) {
                    this.dateControl.setValue(moment(this.control.value).format('YYYY-MM-DD'));
                    this.timeControl.setValue(moment(this.control.value).format('HH:mm'));
                }

                if (!!this.dateControl.value && !this.disabled) {
                    this.timeControl.enable();
                }
        });
    }

    subscribeTimeControlValueChanges() {
        this.timeControl.valueChanges
            .pipe(
                filter(date => !!date),
                debounceTime(500),
                distinctUntilChanged(),
                takeUntil(this.destroyed$)
            ).subscribe((date) => {
                if (!!date && this.times?.filter(range => range.value === date).length < 1) {
                    this.times.push({label: date, value: date});
                    this.times = [...this.times.sort((a, b) => moment(a.value, 'HH:mm').isBefore(moment(b.value, 'HH:mm')) ? -1 : 0)];
                    this.cd.markForCheck();
                }
        });
    }
}

export class MyDateTimePickerMatcher implements ErrorStateMatcher {
    control: AbstractControl;
    picker: MatDatepicker<Date>;

    constructor(control: AbstractControl, picker) {
        this.control = control;
        this.picker = picker;
    }

    isErrorState(dateControl: UntypedFormControl | null): boolean {
        if ((this.control.hasError('required')
                || this.control.hasError('endDateCannotBeBeforeStart')
                || this.control.hasError('beforeMinValidatedRentalStart')
                || this.control.hasError('rentalStartAfterRentalEnd'))
            && ((dateControl.touched && !this.picker.opened) || this.control.touched)) {
            dateControl.setErrors({incorrect: true});
            return true;
        } else {
            dateControl.setValidators([]);
            return false;
        }
    }
}
